import { type ComponentProps, useMemo } from 'react';
import { DayPicker } from 'react-day-picker';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { type Locale, enUS } from 'date-fns/locale';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { genMonths, genYears, getDefaultYearRange } from '@/utilities/date';
import { buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type CalendarProps = ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  startMonth,
  endMonth,
  ...props
}: CalendarProps & {
  startMonth?: Date;
  endMonth?: Date;
}) {
  const { minYear, maxYear } = useMemo(() => getDefaultYearRange(), []);
  const resolvedStartMonth = useMemo(() => {
    return startMonth ?? new Date(minYear, 0, 1);
  }, [minYear, startMonth]);
  const resolvedEndMonth = useMemo(() => {
    return endMonth ?? new Date(maxYear, 11, 31);
  }, [endMonth, maxYear]);

  const months = useMemo(() => {
    let locale: Pick<Locale, 'options' | 'localize' | 'formatLong'> = enUS;
    const { options, localize, formatLong } = props.locale ?? {};
    if (options && localize && formatLong) {
      locale = {
        options,
        localize,
        formatLong,
      };
    }
    return genMonths(locale);
  }, [props.locale]);

  const years = useMemo(() => genYears(resolvedStartMonth, resolvedEndMonth), [resolvedStartMonth, resolvedEndMonth]);

  const isAtStart =
    props.month?.getFullYear() === resolvedStartMonth?.getFullYear() &&
    props.month.getMonth() === resolvedStartMonth.getMonth();

  const isAtEnd =
    props.month?.getFullYear() === resolvedEndMonth?.getFullYear() &&
    props.month.getMonth() === resolvedEndMonth.getMonth();

  const buttonNavClassName = buttonVariants({
    variant: 'outline-solid',
    className: 'absolute top-2 h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100',
  });

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      month={props.month}
      {...props}
      startMonth={resolvedStartMonth}
      endMonth={resolvedEndMonth}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-y-0',
        dropdowns: 'rdp-dropdowns flex',
        dropdown: 'mr-2.5 bg-white dark:bg-neutral-950 rdp-dropdown',
        month: 'space-y-4 mx-2',
        month_caption: 'flex justify-center relative items-center',
        caption_label: 'text-sm font-medium',
        nav: 'space-x-1 flex items-center z-50',
        button_previous: cn(buttonNavClassName, 'left-2', isAtStart && 'pointer-events-none'),
        button_next: cn(buttonNavClassName, 'right-2', isAtEnd && 'pointer-events-none'),
        month_grid: 'w-full border-collapse space-y-1',
        weekdays: 'flex',
        weekday: 'text-text-gray-alternate rounded-md w-8 font-normal text-[0.8rem]',
        week: 'flex w-full mt-2',
        focused: '',
        day: cn(
          'relative p-0 text-center dark:hover:text-text-inverted text-sm focus-within:relative focus-within:z-20',
          props.mode === 'range' ? '[&.day-range-start]:rounded-l-md! [&.day-range-end]:rounded-r-md!' : 'rounded-md',
        ),
        day_button: cn(
          buttonVariants({ variant: 'ghost' }),
          'h-8 rounded-md w-8 p-0 font-normal hover:bg-highlight-hover-color text-inherit! dark:hover:bg-highlight-hover-color',
        ),
        range_start: 'day-range-start dark:text-text-inverted',
        range_end: 'day-range-end dark:text-text-inverted',
        selected: 'bg-highlight-base text-text-base dark:text-text-inverted',
        today: 'bg-ui-light rounded-md text-text-base data-[selected=true]:bg-highlight-medium',
        outside: 'day-outside text-text-gray-alternate',
        disabled: 'text-text-gray opacity-50 hover:text-text-gray!',
        range_middle: 'bg-highlight-base rounded-none dark:text-text-inverted',
        hidden: 'invisible',
        months_dropdown: 'mb-2',
        ...classNames,
      }}
      components={{
        Chevron: ({ orientation }) => {
          const Icon = orientation === 'left' ? ChevronLeftIcon : ChevronRightIcon;
          return <Icon className="icon-4" />;
        },
        MonthCaption: ({ calendarMonth, className: monthCaptionClassname }) => {
          return props.mode === 'range' ? (
            <div className={monthCaptionClassname}>
              {calendarMonth.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </div>
          ) : (
            <div className={monthCaptionClassname}>
              <Select
                defaultValue={calendarMonth.date.getMonth().toString()}
                onValueChange={(value) => {
                  const newDate = new Date(calendarMonth.date);
                  newDate.setMonth(Number.parseInt(value, 10));
                  props.onMonthChange?.(newDate);
                }}
              >
                <SelectTrigger className="w-fit gap-1 border-none text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {months.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                defaultValue={calendarMonth.date.getFullYear().toString()}
                onValueChange={(value) => {
                  const newDate = new Date(calendarMonth.date);
                  newDate.setFullYear(Number.parseInt(value, 10));
                  props.onMonthChange?.(newDate);
                }}
              >
                <SelectTrigger className="w-fit gap-1 border-none text-base">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map((year) => (
                    <SelectItem key={year.value} value={year.value.toString()}>
                      {year.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          );
        },
      }}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
